/**
 * Deduplicated token refresh for 401 retry in API clients.
 *
 * Products use refreshAndGetToken() from their API client layer to get a
 * fresh access token after a 401. Concurrent calls are coalesced so only
 * one Cognito round-trip happens at a time.
 */
import { fetchAuthSession } from "aws-amplify/auth";
let refreshPromise = null;
let onTokenRefreshed;
/**
 * Register a callback invoked after a successful forced token refresh.
 * Used internally by AuthProvider to sync React state when the API client
 * refreshes behind the scenes. Returns the previous callback (if any).
 */
export function registerTokenRefreshedCallback(callback) {
    const prev = onTokenRefreshed;
    onTokenRefreshed = callback;
    return prev;
}
/**
 * Notify that the token was just refreshed. Call after a successful 401 retry
 * so AuthProvider can update its state.
 */
export function notifyTokenRefreshed() {
    onTokenRefreshed?.();
}
/**
 * Force-refresh the auth session and return the access token string.
 *
 * Uses `forceRefresh: true` so Cognito validates the refresh token on its
 * side — this detects revoked tokens (e.g. after AdminUserGlobalSignOut).
 *
 * Concurrent calls are deduplicated — only one network request at a time.
 */
export async function refreshAndGetToken() {
    if (refreshPromise)
        return refreshPromise;
    refreshPromise = (async () => {
        try {
            const session = await fetchAuthSession({ forceRefresh: true });
            return session.tokens?.accessToken?.toString() ?? null;
        }
        finally {
            refreshPromise = null;
        }
    })();
    return refreshPromise;
}
